import { Immutable } from "immer";
import React, { useState } from "react";

import type { ProductTable as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

type ColumnLabel = NonNullable<Value["column_labels"]>[number];
type Row = NonNullable<Value["rows"]>[number];
type CTACell = NonNullable<Value["cta"]>[number];
type CTAButton = NonNullable<CTACell["buttons"]>[number];

/**
 * React port of the sealy ``ProductTable`` block (legacy template
 * ``components/product_tables/product_table.html`` plus the legacy
 * module-eval script ``client/src/ui/product-table.ts``).
 *
 * Interactivity notes (matching legacy *production* behavior):
 *
 * - Tabs: the legacy script added ``prodchart__cell--on`` to every
 *   ``[data-column="0"]`` cell at init time and moved that class (plus the
 *   ``aria-selected`` flip) between columns on tab click. ``--on`` is the
 *   mobile visibility mechanism (see ``_prodchart.scss``: at small
 *   breakpoints cells are ``display: none`` unless ``--on``). We bake the
 *   initial column-0 state into the render and own tab clicks with React
 *   state, scoped per table instance (the legacy script's document-global
 *   selectors leaked across multiple tables; per-instance scoping is a
 *   correctness fix, not a behavior change for the single-table case).
 *
 * - Collapsible rows: the legacy ``.prodchart-trigger`` handler has been a
 *   no-op since the 2020 jQuery removal (#16033, commit a4d29c8b2c): the
 *   jQuery ``$(this).data("id-trigger")`` lookup was rewritten as
 *   ``elem.dataset["id-trigger"]``, but DOM ``dataset`` camelCases keys
 *   (``data-id-trigger`` -> ``dataset.idTrigger``), so the lookup returns
 *   ``undefined`` and the ``[data-extra-id="undefined"]`` query matches
 *   nothing. Production cells therefore stay ``prodchart__cell--closed``
 *   forever. We intentionally reproduce that steady state: the trigger
 *   anchor is rendered for visual parity but implementing a working toggle
 *   would CHANGE long-standing production behavior.
 *
 * The Django ``|cut:" "`` filter (used for ``data-extra-id`` /
 * ``data-id-trigger``) removes every space character; ``stripSpaces``
 * replicates it exactly.
 */
const stripSpaces = (text: string | null | undefined): string =>
    (text ?? "").split(" ").join("");

/**
 * Django ``LinkValue.get_url`` parity: a chosen page wins (``page.url or
 * ""``); otherwise fall back to ``external_url`` (or ``""``).
 */
const getLinkURL = (link: Immutable<CTAButton>["link"]): string => {
    if (link?.page) {
        return link.page.url || "";
    }
    return link?.external_url ?? "";
};

const ColumnHeadCell = (
    props: Immutable<{
        label: ColumnLabel;
        index: number;
        isOn: boolean;
        posturepedicLogoURL: string;
    }>,
): React.JSX.Element => {
    // Legacy: ``{% image column_label.image original as image %}`` then
    // ``image.url`` -- prefer the serialized ``original`` rendition.
    const imageURL =
        props.label.image?.renditions["original"]?.url ??
        props.label.image?.url ??
        "";
    return (
        <div
            data-column={props.index}
            className={`prodchart__cell prodchart__cell--head prodchart__cell--head${
                props.index + 1
            }${props.isOn ? " prodchart__cell--on" : ""}`}
        >
            <div className="prodchart__cell--head-content">
                <span className="prodchart__cell-image">
                    <img
                        src={imageURL}
                        alt={`${props.label.title ?? ""} Bed`}
                        loading="lazy"
                        decoding="async"
                    />
                </span>
                <h5>{props.label.title}</h5>
                {props.label.logo && (
                    <span className="prodchart__cell-badge">
                        {/* The "Posturpedic" alt typo is preserved from the
                            legacy template for parity. */}
                        <img
                            src={props.posturepedicLogoURL}
                            alt="Posturpedic Technology"
                            aria-hidden="true"
                            loading="lazy"
                            decoding="async"
                        />
                    </span>
                )}
                <p>{props.label.copy}</p>
            </div>
        </div>
    );
};

const RowCells = (
    props: Immutable<{
        row: Row;
        presentation: Value["presentation"];
        activeColumn: number;
    }>,
): React.JSX.Element => {
    const extraID = stripSpaces(props.row.title);
    const cells = props.row.cells ?? [];
    return (
        <>
            {props.row.collapsible ? (
                <div
                    data-extra-id={extraID}
                    className="prodchart__cell prodchart__cell--def prodchart__cell--closed"
                >
                    <p>{props.row.title}</p>
                    {/* The trigger is intentionally inert -- see the
                        collapsible-rows note in the component docstring. The
                        legacy href was ``javascript:void(0)``, but React 19
                        unconditionally rewrites ``javascript:`` URLs to a
                        throwing placeholder, so an inert ``#`` +
                        ``preventDefault`` reproduces the production no-op
                        instead. */}
                    <a
                        href="#"
                        className="prodchart-trigger"
                        data-id-trigger={extraID}
                        title={`Toggle ${props.row.title ?? ""} Details`}
                        onClick={(event) => {
                            event.preventDefault();
                        }}
                    >
                        <figure className="prodchart__icon">
                            <svg className="icon-chevron">
                                <use xlinkHref="#icon-chevron" />
                            </svg>
                        </figure>
                    </a>
                    <p className="prodchart-extra">{props.row.copy}</p>
                </div>
            ) : (
                <div
                    className={
                        props.presentation === "mattress_base"
                            ? "prodchart__cell prodchart__cell--def prodchart__cell--closed"
                            : "prodchart__cell prodchart__cell--closed"
                    }
                >
                    {props.row.title}
                </div>
            )}
            {cells.map((cell, cellIndex) => {
                const cellClass = `prodchart__cell${
                    cellIndex === props.activeColumn
                        ? " prodchart__cell--on"
                        : ""
                }`;
                const features = cell.features ?? [];
                return props.row.collapsible ? (
                    <div
                        key={cellIndex}
                        data-column={cellIndex}
                        data-extra-id={extraID}
                        className={cellClass}
                    >
                        {features.map((feature, featureIndex) => (
                            <React.Fragment key={featureIndex}>
                                <p>{feature.title}</p>
                                <p className="prodchart-extra">
                                    {feature.copy}
                                </p>
                            </React.Fragment>
                        ))}
                    </div>
                ) : (
                    <div
                        key={cellIndex}
                        data-column={cellIndex}
                        className={cellClass}
                    >
                        {/* Legacy Django rendered each title on its own
                            indented line, which HTML whitespace collapsing
                            displayed as a single space between titles. */}
                        {features.map((feature) => feature.title).join(" ")}
                    </div>
                );
            })}
        </>
    );
};

const CTAButtonLink = (
    props: Immutable<{ button: CTAButton }>,
): React.JSX.Element => {
    const link = props.button.link;
    return (
        <>
            {props.button.connector && <p>{props.button.connector}</p>}
            {/* ``rel="noreferrer"`` is a deviation from the legacy template
                (which set no rel); it is this repo's lint-enforced
                convention for target="_blank" links. */}
            <a
                className="button"
                target={link?.open_in_new_tab ? "_blank" : undefined}
                rel={link?.open_in_new_tab ? "noreferrer" : undefined}
                href={getLinkURL(link)}
                title={link?.html_title ?? ""}
            >
                {link?.display_name}
            </a>
        </>
    );
};

export const SealyProductTable = (props: Props): React.JSX.Element => {
    const value = props.value;
    const columnLabels = value.column_labels ?? [];
    const rows = value.rows ?? [];
    const cta = value.cta ?? [];
    // Legacy script's post-init steady state: column 0 is active.
    const [activeColumn, setActiveColumn] = useState(0);

    return (
        <section className="prodchart-wrapper ptb">
            {/* title */}
            <div className="lead-in container">
                <h2 className="lead-in__headline">{value.title}</h2>
                <p className="lead-in__copy">{value.copy}</p>
            </div>

            <div className="prodchart prodchart--margin-top prodchart--4cols prodchart--collapse js-prodchartTabs">
                {/* tab buttons for mobile */}
                <div className="Tablist" role="tablist">
                    <button
                        className="Tab hiddenSmall"
                        role="tab"
                        aria-selected="false"
                    >
                        empty
                    </button>
                    {columnLabels.map((label, index) => (
                        <button
                            key={index}
                            className="Tab prodchart__tab"
                            role="tab"
                            aria-selected={
                                index === activeColumn ? "true" : "false"
                            }
                            onClick={() => {
                                setActiveColumn(index);
                            }}
                        >
                            {label.subtitle}
                        </button>
                    ))}
                </div>

                {/* 1st row */}
                <div className="prodchart__cell prodchart__cell--hidden">
                    <h3>EMPTY</h3>
                </div>
                {columnLabels.map((label, index) => (
                    <ColumnHeadCell
                        key={index}
                        label={label}
                        index={index}
                        isOn={index === activeColumn}
                        posturepedicLogoURL={value.posturepedic_logo_url}
                    />
                ))}

                {/* rows */}
                {rows.map((row, rowIndex) => (
                    <RowCells
                        key={rowIndex}
                        row={row}
                        presentation={value.presentation}
                        activeColumn={activeColumn}
                    />
                ))}

                {/* last row */}
                <div className="prodchart__cell prodchart__cell--hidden">
                    empty
                </div>
                {cta.map((ctaCell, index) => (
                    <div
                        key={index}
                        data-column={index}
                        className={`prodchart__cell prodchart__cell--cta${
                            index === activeColumn ? " prodchart__cell--on" : ""
                        }`}
                    >
                        {(ctaCell.buttons ?? []).map((button, buttonIndex) => (
                            <CTAButtonLink key={buttonIndex} button={button} />
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
};
